/**
 * Runs one Genesys popup sign-in (`loginPKCEGrant` with `usePopup`) safely.
 *
 * purecloud-platform-client-v2 260 adds an anonymous window "message"
 * listener for each popup but removes a different function, so every popup
 * sign-in leaves its listener behind. On the next popup the same single-use
 * code is exchanged once per leftover listener, Genesys refuses the repeat
 * (400 invalid_grant) and the SDK clears the token it just got. The SDK also
 * notices a blocked or closed popup only at its own timeout.
 *
 * While the SDK opens the popup, the "message" listeners it adds are recorded
 * and removed once the sign-in settles. A popup that is blocked, or closed
 * before Genesys answered, fails the sign-in at once.
 */

/** Error texts `signInFailureKind` maps to the closed and blocked popup messages. */
export const POPUP_CLOSED_MESSAGE = 'Authentication popup closed before sign-in finished';
export const POPUP_BLOCKED_MESSAGE = 'Authentication popup blocked';

/**
 * Why a sign-in failed, for its message: the admin closed the popup, the popup
 * was blocked or timed out (Genesys SDK "Authentication Popup Timeout"), or anything else.
 */
export function signInFailureKind(error: unknown): 'closed' | 'popup' | 'other' {
  const message = error instanceof Error ? error.message : String(error);
  if (message.includes(POPUP_CLOSED_MESSAGE)) return 'closed';
  return /popup timeout|authentication popup/i.test(message) ? 'popup' : 'other';
}

export const POPUP_WATCH_INTERVAL_MS = 500;
/** Polls in a row that must see the popup closed with no reply, so a reply posted just before closing still counts. */
const CLOSED_POLLS_TO_FAIL = 3;

type AddListener = Window['addEventListener'];
type OpenWindow = Window['open'];

interface GuardWindow {
  addEventListener: AddListener;
  removeEventListener: Window['removeEventListener'];
  open: OpenWindow;
  setInterval: (handler: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
}

function isAuthPopupReply(event: Event): boolean {
  const data = (event as MessageEvent).data as { name?: unknown; type?: unknown } | null;
  return Boolean(data && typeof data === 'object' && data.name === 'gc_auth_popup' && data.type === 'message');
}

function isAuthorizeUrl(url: string | URL | undefined): boolean {
  return typeof url !== 'undefined' && String(url).includes('/oauth/authorize');
}

export async function runPopupSignIn<T>(login: () => Promise<T>, win: GuardWindow = window as unknown as GuardWindow): Promise<T> {
  const added: EventListener[] = [];
  const originalAdd = win.addEventListener;
  const originalOpen = win.open;
  let patched = true;
  let opened = false;
  let popup: Window | null = null;
  let replied = false;
  let closedPolls = 0;
  let watch: number | null = null;

  const unpatch = () => {
    if (!patched) return;
    patched = false;
    win.addEventListener = originalAdd;
    win.open = originalOpen;
  };

  win.addEventListener = function (this: unknown, type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) {
    if (type !== 'message' || !listener) return originalAdd.call(win, type, listener, options);
    const wrapped: EventListener = (event) => {
      if (isAuthPopupReply(event)) replied = true;
      if (typeof listener === 'function') listener.call(win, event);
      else listener.handleEvent(event);
    };
    added.push(wrapped);
    return originalAdd.call(win, type, wrapped, options);
  } as AddListener;

  win.open = function (this: unknown, url?: string | URL, target?: string, features?: string) {
    const opener = originalOpen.call(win, url, target, features);
    if (isAuthorizeUrl(url)) {
      opened = true;
      popup = opener;
      // The SDK adds its listener just before it opens the popup.
      unpatch();
    }
    return opener;
  } as OpenWindow;

  const watcher = new Promise<never>((_, reject) => {
    watch = win.setInterval(() => {
      if (!opened || replied) return;
      if (!popup) {
        reject(new Error(POPUP_BLOCKED_MESSAGE));
        return;
      }
      let closed = false;
      try {
        closed = popup.closed;
      } catch {
        closed = false;
      }
      closedPolls = closed ? closedPolls + 1 : 0;
      if (closedPolls >= CLOSED_POLLS_TO_FAIL) reject(new Error(POPUP_CLOSED_MESSAGE));
    }, POPUP_WATCH_INTERVAL_MS);
  });
  watcher.catch(() => undefined);

  try {
    const sdkLogin = login();
    // When the watcher wins, the SDK's own promise still settles later (its timeout); keep that quiet.
    sdkLogin.catch(() => undefined);
    return await Promise.race([sdkLogin, watcher]);
  } finally {
    unpatch();
    if (watch !== null) win.clearInterval(watch);
    for (const listener of added) win.removeEventListener('message', listener);
  }
}
